import { BaseSocketService, SocketServiceConfig } from '../index';

// ----------- Type Definitions -----------
export interface RunLog {
    run_log_id: string;
    status: 'success' | 'error' | 'running' | 'completed' | 'failed';
    message: string;
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    start_timestamp: string;
    end_timestamp?: string;
    duration_ms?: number;
    progress?: number;
    total_records?: number;
    processed_records?: number;
    dealer_id?: string;
    campaign_id?: string;
    created_at: string;
    updated_at: string;
}

export interface RunLogEvent {
    run_log_id: string;
    runId?: string; // Alternative property name from backend
    id?: string; // Alternative property name from backend
    status: 'success' | 'error' | 'running' | 'completed' | 'failed';
    message: string;
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    start_timestamp: string;
    end_timestamp?: string;
    duration_ms?: number;
    progress?: number;
    total_records?: number;
    processed_records?: number;
    timestamp: string;
    created_at?: string; // Alternative property name from backend
    updated_at?: string; // Alternative property name from backend
}

export interface RunLogRequest {
    dealer_id: string;
    campaign_id: string;
    action: 'start' | 'stop' | 'pause' | 'resume';
    parameters?: Record<string, any>;
}

export interface SubscribeRequest {
    runId: string;
    token?: string;
}

export interface RunLogSubscription {
    runId: string;
    callback: (data: RunLogEvent) => void;
    active: boolean;
}

export interface JobSubscription {
    jobId: string;
    active: boolean;
}

// ----------- RunLogs Socket Service -----------
class RunLogsSocketService extends BaseSocketService {
    private static instance: RunLogsSocketService;
    private subscriptions: Map<string, RunLogSubscription> = new Map();
    private jobSubscriptions: Map<string, JobSubscription> = new Map();
    private runLogCache: Map<string, RunLogEvent> = new Map();

    constructor(config: SocketServiceConfig) {
        super(config);
    }

    // Singleton pattern
    static getInstance(config?: SocketServiceConfig): RunLogsSocketService {
        if (!RunLogsSocketService.instance) {
            if (!config) {
                throw new Error('RunLogsSocketService requires config for first initialization');
            }
            RunLogsSocketService.instance = new RunLogsSocketService(config);
        }
        return RunLogsSocketService.instance;
    }

    // Service name
    getServiceName(): string {
        return 'RunLogsSocketService';
    }

    // Events this service handles (matching backend structure)
    getEvents(): string[] {
        return [
            'run_log_event',           // Main event for all run log updates
            'run_log_started',         // When run starts
            'run_log_completed',       // When run completes successfully
            'run_log_failed',          // When run fails
            'run_log_progress',        // Progress updates
            'run_log_error',           // Error events
            'queue_job_lifecycle',    // Job queue lifecycle (e.g. NURTURING_CANDIDATES_PICKER)
            'subscribe_success',       // Subscription confirmation
            'subscribe_error',         // Subscription error
            'unsubscribe_success',     // Unsubscription confirmation
            'unsubscribe_error'        // Unsubscription error
        ];
    }

    // ----------- Run Log Events (Backend Structure) -----------

    // Main event listener for all run log updates
    onRunLogEvent(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_event', (data: RunLogEvent) => {
            // Cache the latest event for this run log
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for run log started
    onRunLogStarted(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_started', (data: RunLogEvent) => {
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for run log completed
    onRunLogCompleted(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_completed', (data: RunLogEvent) => {
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for run log failed
    onRunLogFailed(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_failed', (data: RunLogEvent) => {
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for run log progress updates
    onRunLogProgress(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_progress', (data: RunLogEvent) => {
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for run log errors
    onRunLogError(callback: (data: RunLogEvent) => void): void {
        this.on('run_log_error', (data: RunLogEvent) => {
            if (data.run_log_id) {
                this.runLogCache.set(data.run_log_id, data);
            }
            callback(data);
        });
    }

    // Listen for subscription success
    onSubscribeSuccess(callback: (data: { runId: string; message: string }) => void): void {
        this.on('subscribe_success', callback);
    }

    // Listen for subscription error
    onSubscribeError(callback: (data: { runId: string; error: string }) => void): void {
        this.on('subscribe_error', callback);
    }

    // Listen for unsubscription success
    onUnsubscribeSuccess(callback: (data: { runId: string; message: string }) => void): void {
        this.on('unsubscribe_success', callback);
    }

    // Listen for unsubscription error
    onUnsubscribeError(callback: (data: { runId: string; error: string }) => void): void {
        this.on('unsubscribe_error', callback);
    }

    // ----------- Run Log Actions (Backend Structure) -----------

    // Subscribe to specific run log updates (matches backend room-based subscription)
    subscribeToRunLog(runId: string, callback?: (data: RunLogEvent) => void, token?: string): void {
        if (!runId) {
            return;
        }

        // Check if already subscribed
        if (this.subscriptions.has(runId)) {
            console.log(`📡 Already subscribed to run log: ${runId}`);
            return;
        }

        // Store subscription for management
        if (callback) {
            this.subscriptions.set(runId, {
                runId,
                callback,
                active: true
            });
        } else {
            // If no callback provided, create a placeholder subscription for persistence
            this.subscriptions.set(runId, {
                runId,
                callback: () => {}, // Empty callback
                active: true
            });
        }

        // Subscribe to specific run_id room (like backend)
        this.emit('subscribe', runId);
    }

    // Unsubscribe from specific run log updates
    unsubscribeFromRunLog(runId: string): void {
        if (!runId) {
            return;
        }

        // Check if subscribed
        if (!this.subscriptions.has(runId)) {
            return;
        }

        // Remove subscription
        this.subscriptions.delete(runId);
        
        // Unsubscribe from specific run_id room (like backend)
        this.emit('unsubscribe', runId);
    }

    // Subscribe to job updates (e.g. NURTURING_CANDIDATES_PICKER jobId from API response)
    subscribeToJob(jobId: string): void {
        if (!jobId) {
            return;
        }

        if (this.jobSubscriptions.has(jobId)) {
            console.log(`📡 Already subscribed to job: ${jobId}`);
            return;
        }

        this.jobSubscriptions.set(jobId, { jobId, active: true });
        this.emit('subscribe', jobId);
    }

    // Unsubscribe from job updates
    unsubscribeFromJob(jobId: string): void {
        if (!jobId) {
            return;
        }

        if (!this.jobSubscriptions.has(jobId)) {
            return;
        }

        this.jobSubscriptions.delete(jobId);
        this.emit('unsubscribe', jobId);
    }

    // Start a run log (this would typically be an API call, but keeping for completeness)
    startRunLog(request: RunLogRequest): void {
        if (!request.dealer_id || !request.campaign_id) {
            return;
        }
        this.emit('start_run_log', request);
    }

    // Stop a run log (this would typically be an API call, but keeping for completeness)
    stopRunLog(runLogId: string): void {
        if (!runLogId) {
            return;
        }
        this.emit('stop_run_log', { run_log_id: runLogId });
    }

    // Get run log status (this would typically be an API call, but keeping for completeness)
    getRunLogStatus(runLogId: string): void {
        if (!runLogId) {
            return;
        }
        this.emit('get_run_log_status', { run_log_id: runLogId });
    }

    // Get all active run logs for a dealer (this would typically be an API call)
    getActiveRunLogs(dealerId: string): void {
        if (!dealerId) {
            return;
        }
        this.emit('get_active_run_logs', { dealer_id: dealerId });
    }

    // ----------- Utility Methods (Backend Structure) -----------

    // Get run logs by status (API call, not socket event)
    getRunLogsByStatus(dealerId: string, status: RunLog['status']): void {
        if (!dealerId || !status) {
            return;
        }
        this.emit('get_run_logs_by_status', { dealer_id: dealerId, status });
    }

    // Get run logs by campaign (API call, not socket event)
    getRunLogsByCampaign(dealerId: string, campaignId: string): void {
        if (!dealerId || !campaignId) {
            return;
        }
        this.emit('get_run_logs_by_campaign', { dealer_id: dealerId, campaign_id: campaignId });
    }

    // Clear completed run logs (API call, not socket event)
    clearCompletedRunLogs(dealerId: string): void {
        if (!dealerId) {
            return;
        }
        this.emit('clear_completed_run_logs', { dealer_id: dealerId });
    }

    // Get run log statistics (API call, not socket event)
    getRunLogStats(dealerId: string, timeRange?: { start: string; end: string }): void {
        if (!dealerId) {
            return;
        }
        this.emit('get_run_log_stats', { dealer_id: dealerId, time_range: timeRange });
    }

    // ----------- Batch Operations -----------

    // Start multiple run logs
    startMultipleRunLogs(requests: RunLogRequest[]): void {
        if (!requests || requests.length === 0) {
            return;
        }
        this.emit('start_multiple_run_logs', { requests });
    }

    // Stop multiple run logs
    stopMultipleRunLogs(runLogIds: string[]): void {
        if (!runLogIds || runLogIds.length === 0) {
            return;
        }
        this.emit('stop_multiple_run_logs', { run_log_ids: runLogIds });
    }

    // Pause multiple run logs
    pauseMultipleRunLogs(runLogIds: string[]): void {
        if (!runLogIds || runLogIds.length === 0) {
            return;
        }
        this.emit('pause_multiple_run_logs', { run_log_ids: runLogIds });
    }

    // Resume multiple run logs
    resumeMultipleRunLogs(runLogIds: string[]): void {
        if (!runLogIds || runLogIds.length === 0) {
            return;
        }
        this.emit('resume_multiple_run_logs', { run_log_ids: runLogIds });
    }

    // ----------- Cache Management -----------

    // Get cached run log event
    getCachedRunLog(runLogId: string): RunLogEvent | undefined {
        return this.runLogCache.get(runLogId);
    }

    // Get all cached run logs
    getAllCachedRunLogs(): Map<string, RunLogEvent> {
        return new Map(this.runLogCache);
    }

    // Clear cache for specific run log
    clearCacheForRunLog(runLogId: string): void {
        this.runLogCache.delete(runLogId);
    }

    // Clear all cache
    clearAllCache(): void {
        this.runLogCache.clear();
    }

    // ----------- Subscription Management -----------

    // Get all active subscriptions
    getActiveSubscriptions(): RunLogSubscription[] {
        return Array.from(this.subscriptions.values()).filter(sub => sub.active);
    }

    // Check if subscribed to a specific run log
    isSubscribedToRunLog(runId: string): boolean {
        return this.subscriptions.has(runId) && this.subscriptions.get(runId)!.active;
    }

    // Unsubscribe from all run logs
    unsubscribeFromAll(): void {
        this.subscriptions.forEach((subscription, runId) => {
            this.unsubscribeFromRunLog(runId);
        });
    }

    // Restore subscriptions from a list of run IDs
    restoreSubscriptions(runIds: string[]): void {
        if (!runIds || runIds.length === 0) {
            return;
        }
        
        runIds.forEach(runId => {
            if (!this.subscriptions.has(runId)) {
                this.subscribeToRunLog(runId);
            }
        });
    }

    // ----------- Event History Methods (Backend Structure) -----------

    // Get run log specific events
    getRunLogEvents(runLogId?: string): any[] {
        const events = this.getEventHistory();
        if (runLogId) {
            return events.filter(event =>
                event.data?.run_log_id === runLogId ||
                event.data?.runId === runLogId
            );
        }
        return events.filter(event =>
            this.getEvents().includes(event.event)
        );
    }

    // Get events by type
    getEventsByType(eventType: string): any[] {
        return this.getEventHistory().filter(event =>
            event.event === eventType
        );
    }

    // Get recent run log events (last N events)
    getRecentRunLogEvents(count: number = 10): any[] {
        const runLogEvents = this.getEventHistory().filter(event =>
            this.getEvents().includes(event.event)
        );
        return runLogEvents.slice(-count);
    }

    // Get main run_log_event events (most important)
    getMainRunLogEvents(): any[] {
        return this.getEventHistory().filter(event =>
            event.event === 'run_log_event'
        );
    }

    // Get subscription events
    getSubscriptionEvents(): any[] {
        return this.getEventHistory().filter(event =>
            event.event === 'subscribe_success' ||
            event.event === 'subscribe_error' ||
            event.event === 'unsubscribe_success' ||
            event.event === 'unsubscribe_error'
        );
    }

    // ----------- Override disconnect to clean up subscriptions -----------
    disconnect(): void {
        // Unsubscribe from all run logs before disconnecting
        this.unsubscribeFromAll();

        // Unsubscribe from all jobs (copy keys to avoid mutation during iteration)
        Array.from(this.jobSubscriptions.keys()).forEach(jobId => {
            this.unsubscribeFromJob(jobId);
        });

        // Clear cache
        this.clearAllCache();

        // Call parent disconnect
        super.disconnect();
    }
}

export default RunLogsSocketService; 